import React, { useMemo, useEffect } from "react";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import ChatSidebar from "../ChatSidebar";
import RichTextEditor from "@src/features/libraryItem/course/RichTextEditor";
import TiptapEditorContextProvider from "@src/features/libraryItem/course/contexts/TiptapEditorContext";
import { ChatSidebarProps } from "../ChatSidebar";
import { debounce } from "lodash";
import { cn } from "@src/lib/utils/cn";
import ProgressBarWithStepUpdates from "@src/lib/ui/ProgressBarWithStepUpdates";
import { useChatContext } from "@src/features/assistantChat/ChatRuntimeProvider";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { AiChatType } from "@src/types.generated";
import Spacer from "@src/lib/ui/Spacer";
import CanvasEmptyState from "./CanvasEmptyState";

interface CanvasProps {
  canvasHtmlContent?: string;
  onCanvasContentChange?: (content: string) => void;
  title?: string;
  subtitle?: string;
  onAction?: () => void;
  onActionLoading?: boolean;
  actionLabel?: string;
  actionIcon?: React.ReactNode;
  showConversionProgress?: boolean;
}

export interface ChatCanvasProps {
  canvasProps?: CanvasProps;
  chatSidebarProps: ChatSidebarProps;
}

export const ChatCanvas: React.FC<ChatCanvasProps> = ({
  canvasProps = {},
  chatSidebarProps,
}) => {
  const {
    canvasHtmlContent,
    onCanvasContentChange,
    title: canvasTitle,
    subtitle: canvasSubtitle,
    onAction: onCanvasAction,
    onActionLoading: onCanvasActionLoading,
    actionLabel: canvasActionLabel,
    actionIcon: canvasActionIcon,
    showConversionProgress = false,
  } = canvasProps;

  const { chatType, status } = useChatContext();
  const { selectedSession } = useChatSidebar(chatType);

  const isOutlineGenerating =
    selectedSession?.artifactLoading || status === "streaming";

  const debouncedOnCanvasContentChange = useMemo(() => {
    if (!onCanvasContentChange) return undefined;
    return debounce(onCanvasContentChange, 1000);
  }, [onCanvasContentChange]);

  useEffect(() => {
    return () => {
      debouncedOnCanvasContentChange?.cancel();
    };
  }, [debouncedOnCanvasContentChange]);
  return (
    <div className="bg-background flex flex-1 self-stretch">
      <div className="flex flex-1 flex-col">
        {(canvasTitle || canvasSubtitle || onCanvasAction) && (
          <div className="border-border flex h-12 items-center justify-between border-b px-4">
            <div className="flex items-center gap-2">
              {canvasSubtitle && (
                <Text
                  variant="compact-title"
                  className="text-muted-foreground"
                  data-testid="canvas-subtitle"
                >
                  {canvasSubtitle}
                </Text>
              )}
            </div>
            {onCanvasAction && !showConversionProgress && (
              <Button
                onClick={onCanvasAction}
                size="sm"
                variant="purple"
                disabled={onCanvasActionLoading || isOutlineGenerating}
                loading={onCanvasActionLoading}
                data-testid="canvas-action-button"
              >
                {canvasActionIcon}
                {canvasActionLabel}
              </Button>
            )}
          </div>
        )}
        {showConversionProgress ? (
          <div className="flex flex-1 items-center justify-center overflow-hidden">
            <ProgressBarWithStepUpdates
              totalDurationSeconds={getConversionDuration(chatType)}
              steps={getConversionSteps(chatType)}
              isConversionInProgress
            />
          </div>
        ) : (
          <div className="relative flex flex-1 self-stretch">
            {selectedSession?.artifactLoading &&
              !selectedSession?.streamingError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <CanvasEmptyState
                    isLoading={selectedSession?.artifactLoading}
                  />
                </div>
              )}
            {!selectedSession?.artifactLoading &&
              selectedSession?.streamingError &&
              !canvasHtmlContent && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Text variant="compact-title" className="text-red-7">
                    Error loading your canvas
                  </Text>
                </div>
              )}
            <ScrollableFlexGrow
              className="flex-1 self-stretch"
              innerClassName={cn(
                "items-center flex flex-col items-center justify-start",
                selectedSession?.artifactLoading &&
                  "opacity-0 pointer-events-none",
              )}
            >
              <div className="mx-[12px] w-full max-w-[600px] pt-6 flex flex-col flex-1 ">
                <TiptapEditorContextProvider>
                  <RichTextEditor
                    text={canvasHtmlContent || ""}
                    disabled={selectedSession?.artifactLoading}
                    hasEditContentPermission={true}
                    onChange={debouncedOnCanvasContentChange}
                    placeholder="Write your outline here..."
                    className="rich-text-opus-doc-editor pb-[100px] flex-1 self-stretch"
                    editorParentClassName="flex-1 pb-[100px]"
                    disableTextVariants
                    data-testid="ai-canvas-editor"
                  />
                </TiptapEditorContextProvider>
              </div>
            </ScrollableFlexGrow>
          </div>
        )}
      </div>
      {/* Right side - Chat sidebar */}
      <div
        className={cn(
          "w-96 border-l border-border flex flex-col bg-white",
          showConversionProgress && "opacity-50 pointer-events-none",
        )}
      >
        <div className="flex-1">
          <ChatSidebar {...chatSidebarProps} />
        </div>
      </div>
    </div>
  );
};

const getConversionDuration = (chatType: AiChatType): number => {
  if (chatType === AiChatType.ModuleOutline) {
    return 200;
  } else if (chatType === AiChatType.TaskListOutline) {
    return 45;
  }
  return 25;
};

const getConversionSteps = (
  chatType: AiChatType,
) => {
  switch (chatType) {
    case AiChatType.ModuleOutline:
      return MODULE_CONTENT_CONVERSION_STEPS;
    case AiChatType.TaskListOutline:
      return getTaskListConversionSteps();
    case AiChatType.AuditOutline:
      return AUDIT_CONVERSION_STEPS;
    case AiChatType.CheckInOutline:
      return CHECK_IN_CONVERSION_STEPS;
    case AiChatType.CourseOutline:
    default:
      return COURSE_CONTENT_CONVERSION_STEPS;
  }
};

const COURSE_CONTENT_CONVERSION_STEPS = [
  {
    threshold: 0,
    message: (
      <div className="flex flex-col items-center gap-2">
        <Text
          variant="display-bold"
          className="text-gray-10"
          data-testid="course-conversion-progress"
        >
          Course conversion in progress
        </Text>
        <Spacer axis="horizontal" size={16} />
        <Text variant="body-title" className="text-gray-7">
          This may take around 30 seconds.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          Please do not close this window or your session will be lost.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          You will be redirected to the course builder on completion.
        </Text>
      </div>
    ),
  },
];

const MODULE_CONTENT_CONVERSION_STEPS = [
  {
    threshold: 0,
    message: (
      <div className="flex flex-col items-center gap-2">
        <Text variant="display-bold" className="text-gray-10">
          Module conversion in progress
        </Text>
        <Spacer axis="horizontal" size={16} />
        <Text variant="body-title" className="text-gray-7">
          This may take a couple minutes.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          Please do not close this window or your session will be lost.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          You will be redirected to the module builder on completion.
        </Text>
      </div>
    ),
  },
];

const getTaskListConversionSteps = () => [
  {
    threshold: 0,
    message: (
      <div className="flex flex-col items-center gap-2">
        <Text variant="display-bold" className="text-gray-10">
          Checklist generation in progress
        </Text>
        <Spacer axis="horizontal" size={16} />
        <Text variant="body-title" className="text-gray-7">
          This may take around 30 seconds.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          Please do not close this window or your session will be lost.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          You will be redirected to the checklist on completion.
        </Text>
      </div>
    ),
  },
];

const AUDIT_CONVERSION_STEPS = [
  {
    threshold: 0,
    message: (
      <div className="flex flex-col items-center gap-2">
        <Text variant="display-bold" className="text-gray-10">
          Audit generation in progress
        </Text>
        <Spacer axis="horizontal" size={16} />
        <Text variant="body-title" className="text-gray-7">
          This may take around 30 seconds.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          Please do not close this window or your session will be lost.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          You will be redirected to the audit on completion.
        </Text>
      </div>
    ),
  },
];

const CHECK_IN_CONVERSION_STEPS = [
  {
    threshold: 0,
    message: (
      <div className="flex flex-col items-center gap-2">
        <Text variant="display-bold" className="text-gray-10">
          Check-in generation in progress
        </Text>
        <Spacer axis="horizontal" size={16} />
        <Text variant="body-title" className="text-gray-7">
          This may take around 30 seconds.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          Please do not close this window or your session will be lost.
        </Text>
        <Text variant="body-title" className="text-gray-7">
          You will be redirected to the check-in on completion.
        </Text>
      </div>
    ),
  },
];

export default ChatCanvas;
